// ContactAvatar — a contact's photo, or something deliberate in its place.
//
// A blank circle reads as a loading failure, so there is always something to
// look at: the photo, else initials, else a role icon. The placeholder is
// styled in the brand terracotta for both Professional and Local contacts.
import React, { useState } from "react";
import { View, Image, StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { theme } from "../theme/theme";
import Icon from "./Icon";

/**
 * Initials for the avatar fallback.
 *
 * People: first and last name (Bruce Shockman → BS).
 * Businesses: first two words (Mobile 1 Auto-Calling and Mechanical → M1).
 * The last word of a business name is usually generic — Services, Mechanical —
 * so first-and-last produced MM on that listing and hid the number.
 */
export const initialsFromName = (name, contactType = "member") => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const second = contactType === "business" ? parts[1] : parts[parts.length - 1];
  return `${parts[0][0]}${second[0]}`.toUpperCase();
};

const ContactAvatar = ({
  uri,
  name,
  fallbackIcon = "user",
  contactType = "member",
  size = 40,
  style,
}) => {
  const [failed, setFailed] = useState(false);
  const showPhoto = Boolean(uri) && !failed;
  const initials = initialsFromName(name, contactType);

  const fallbackBgColor = theme.colors.primary;

  const circle = {
    width: size,
    height: size,
    borderRadius: size / 2,
  };

  return (
    <View style={[style, styles.wrap]}>
      <View
        style={[
          styles.avatar,
          circle,
          !showPhoto && { backgroundColor: fallbackBgColor },
        ]}
      >
        {showPhoto ? (
          <Image
            source={{ uri }}
            style={circle}
            onError={() => setFailed(true)}
            accessibilityIgnoresInvertColors
          />
        ) : initials ? (
          <Text style={[styles.initials, { fontSize: Math.round(size * 0.325) }]}>
            {initials}
          </Text>
        ) : (
          <Icon
            name={fallbackIcon}
            size={Math.round(size * 0.45)}
            color={theme.colors.onPrimary}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    justifyContent: "center",
    alignItems: "center",
  },
  avatar: {
    overflow: "hidden",
    backgroundColor: theme.colors.surfaceVariant,
    justifyContent: "center",
    alignItems: "center",
  },
  initials: {
    fontWeight: "700",
    fontFamily: "Poppins",
    color: theme.colors.onPrimary,
  },
});

export default ContactAvatar;
